import React, {
  useCallback, useReducer, useRef, useContext, useState
} from 'react';
import './consumer-privacy-act-form.scss';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { Input } from '@thd-olt-component-react/input';
import { Col, Row, Button } from '@thd-olt-component-react/core-ui';
import { Alert } from '@one-thd/sui-atomic-components';
import proptypes from 'prop-types';
const { string, bool } = proptypes;
import {
  containsLettersRegex,
  emailRegex,
  notEmptyRegex,
  zipCodeRegex
} from '@thd-olt-functional/utils';
import { CpaStateDropdown } from './CpaStateDropdown/CpaStateDropdown';
import {
  deliveryMethodOptions,
  getMyInformationOptionVal,
  lexisNexisStatus,
  requestTypeOptions,
  scrollToRef,
  validate,
  cityValidation,
  validateRequiredInput,
  cpraRequestTypeOptions,
  OptOutOfSaleOptionVal,
  limitUseSpiOptionVal,
  imageBaseUrl,
} from '../constants/constants';
import { CpaRadioGroup } from './CpaRadioGroup/CpaRadioGroup';
import { CpaRecaptcha } from './CpaRecaptcha/CpaRecaptcha';
import { CpaPhoneInput } from './CpaPhoneInput/CpaPhoneInput';
import { CpaAddressInput } from './CpaAddressInput/CpaAddressInput';
import { CpaConfirmation } from './CpaConfirmation/CpaConfirmation';
import { CpaSubmitButton } from './CpaSubmitButton/CpaSubmitButton';
import { CpaEmailAddressInput } from './CpaEmailAddressInput/CpaEmailAddressInput';
import { CpaLexisNexisQuestions } from './CpaLexisNexisQuestions/CpaLexisNexisQuestions';
import { InformationalMessage } from './InformationalMessage/InformationalMessage';
import { createOptOutCookies } from '../util/PrivacyCookieUtil';
import webviewSDK from 'webview_sdk';

/* global cookieUtils */
const requestsWithoutLexisNexis = [OptOutOfSaleOptionVal, limitUseSpiOptionVal];

const ConsumerPrivacyActForm = ({ recaptchaKey, cpraEnabled = true }) => {

  const initialState = {
    addressLine1: {
      value: '',
      valid: false
    },
    addressLine2: {
      value: '',
      valid: true
    },
    city: {
      value: '',
      valid: false
    },
    email: {
      value: '',
      valid: false
    },
    firstName: {
      value: '',
      valid: false
    },
    lastName: {
      value: '',
      valid: false
    },
    phone: {
      value: '',
      valid: true
    },
    requestType: {
      value: '',
      valid: false
    },
    stateOfResidence: {
      value: '',
      valid: false
    },
    state: {
      value: '',
      valid: false
    },
    zipCode: {
      value: '',
      valid: false
    },
    deliveryMethod: {
      value: '',
      valid: false
    },
    recaptchaVerified: {
      value: false,
      valid: false
    },
    submitEnabled: {
      value: false,
      valid: true
    },
    submitSuccess: {
      value: false,
      valid: true
    },
    submitNotProcessing: {
      value: true,
      valid: true
    },
    submitError: {
      value: false,
      valid: true
    },
    lexisNexisStatus: {
      value: '',
      valid: true
    },
    dataCorrectionComments: {
      value: '',
      valid: true
    }
  };

  const topOfFormRef = useRef();

  const navigate = (link) => {
    window.location.href = link;
    return true;
  };

  const reducer = (state, action) => {

    const newInputState = {
      ...state,
      [action.input]: {
        value: action.value,
        valid: action.valid
      }
    };

    // unrequire fields if the request type is not correction
    if (action.input === 'requestType') {
      newInputState.dataCorrectionComments.valid = true;
      newInputState.dataCorrectionComments.value = '';
      if (action.value === 'Correct My Information') {
        newInputState.dataCorrectionComments.valid = false;
      }
    }

    let submitEnabled = true;
    Object.keys(newInputState)
      .forEach((key) => {
        submitEnabled = submitEnabled && newInputState[key].valid;
      });

    return {
      ...newInputState,
      submitEnabled: {
        value: submitEnabled,
        valid: true
      }
    };
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const { channel } = useContext(ExperienceContext);
  const isWebviewSDKEnabled = useConfigService('fs:EnableWebviewSDKCPRAOptOut');


  const inputChangeHandler = useCallback(
    (value, identifier, valid) => {
      dispatch({
        value,
        input: identifier,
        valid
      });
    }, [dispatch]);

  const [requestPendingBanner, setRequestPendingBanner] = useState(false);

  const validateDataCorrectionCommentField = (event) => {
    return validate(event, notEmptyRegex) && validateRequiredInput(event);
  };

  const validateInformationRequest = (fieldValue) => {
    const { requestType } = state;
    return requestType.value === getMyInformationOptionVal ? !!fieldValue : true;
  };

  const defaultValidation = (fieldValue) => fieldValue.match(containsLettersRegex);

  const dataCorrectionValidation = (fieldValue) => fieldValue.match(notEmptyRegex);

  const zipValidation = (fieldValue) => validate(fieldValue, zipCodeRegex);

  const firstNameChangeHandler = (event) => inputChangeHandler(event, 'firstName',
    validate(event, containsLettersRegex));

  const lastNameChangeHandler = (event) => inputChangeHandler(event, 'lastName', validate(event, containsLettersRegex));

  const emailChangeHandler = (event) => inputChangeHandler(event, 'email', validate(event, emailRegex));

  const addrLine2ChangeHandler = (event) => inputChangeHandler(event, 'addressLine2', true);

  const cityChangeHandler = (event) => inputChangeHandler(event, 'city', validate(event, containsLettersRegex)
    && validateRequiredInput(event));

  const zipChangeHandler = (event) => inputChangeHandler(event, 'zipCode', validate(event, zipCodeRegex));

  const dataCorrectionCommentsChangeHandler = (event) => inputChangeHandler(event, 'dataCorrectionComments',
    validateDataCorrectionCommentField(event));

  const formatHeader = (header) => {
    return (<span>{header}<span style={{ color: 'red' }}>*</span></span>);
  };

  const handleRequestSuccess = (json) => {
    if (json) {
      const lexisNexisStatusConstant = (json.requestTypes.some((req) => requestsWithoutLexisNexis.includes(req)))
        ? lexisNexisStatus.passed
        : json.lnResponse.transactionStatus;

      dispatch({
        value: lexisNexisStatusConstant,
        input: 'lexisNexisStatus',
        valid: true
      });
      dispatch({
        value: json,
        input: 'response',
        valid: true
      });
      dispatch({
        value: true,
        input: 'submitSuccess',
        valid: true
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const {
      email,
      firstName,
      lastName,
      addressLine1,
      addressLine2,
      city,
      deliveryMethod,
      stateOfResidence,
      phone,
      requestType,
      zipCode,
      dataCorrectionComments
    } = state;

    const requestBody = {
      email: email.value,
      firstName: firstName.value,
      lastName: lastName.value,
      requestTypes: [requestType.value],
      addressLine1: addressLine1.value,
      addressLine2: addressLine2.value,
      city: city.value,
      state: state.state.value,
      zip: zipCode.value,
      deliveryMethod: deliveryMethod.value,
      stateOfResidence: stateOfResidence.value,
      phone: phone.value,
      dataCorrectionComments: dataCorrectionComments.value
    };

    dispatch({
      value: false,
      input: 'submitNotProcessing',
      valid: false
    });
    if (requestType.value === OptOutOfSaleOptionVal) {
      createOptOutCookies(cookieUtils.fed_CreateCookie, window.location.hostname);
      window.LIFE_CYCLE_EVENT_BUS?.lifeCycle.trigger('NATIVE_APP.OPT_OUT', { requestType: 'Opt Out' });

      if (isWebviewSDKEnabled) {
        const startTime = window?.performance?.now();
        
        const handleNativeTimestamp = async () => {
          const nativeStartTime = await window?.wvSDK?.storage.get('OPT_OUT_TIMESTAMP');
          const endTime = window?.performance?.now().toString();
          window?.newrelic?.addPageAction('WVSDK_OPT_OUT_PERFORMANCE', {
            platform: window?.wvSDK?.platform,
            duration: endTime - nativeStartTime
          });
        }

        window?.wvSDK?.storage.set('OPT_OUT_TIMESTAMP', startTime?.toString())
          .then(() => {
            const platform = window?.wvSDK?.platform;
            if (platform === 'ios') {
              window.wvSDK?.iosPlatformHandler("NATIVE_APP.OPT_OUT", { requestType: 'Opt Out' })
                .then(() => {
                  handleNativeTimestamp();
                })
                .catch((e) => console.error(`Error in ${platform} opt out handler`, e));
            } else if (platform === 'android') {
              window.wvSDK?.androidPlatformHandler("NATIVE_APP.OPT_OUT", { requestType: 'Opt Out' })
                .then(() => {
                  handleNativeTimestamp();
                })
                .catch((e) => console.error(`Error in ${platform} opt out handler`, e));
            }
          })
          .catch((e) => console.error('Error setting storage key OPT_OUT_TIMESTAMP', e))
          .finally(() => {
            const endTime = window?.performance?.now();
            window?.newrelic?.addPageAction('WEBVIEW_SDK_OPT_OUT_PERFORMANCE', {
              platform: window?.wvSDK?.platform,
              duration: endTime - startTime,
              timestamp: startTime
            });
          });

        const writeReadStoragePerformance = async () => {
          const microsecondTimestamp = window?.performance?.now();
          await window?.wvSDK?.storage.set('WVSDK_WRITE_READ_TIMESTAMP', microsecondTimestamp);
          const microsecondTimestampPostSet = window?.performance?.now();
          const storageGet = await window?.wvSDK?.storage.get('WVSDK_WRITE_READ_TIMESTAMP');
          const microsecondTimestampPostGet = window?.performance?.now();
          window?.newrelic?.addPageAction('WVSDK_WRITE_READ_PERFORMANCE', {
            platform: window?.wvSDK?.platform,
            write: microsecondTimestampPostSet - microsecondTimestamp,
            read: microsecondTimestampPostGet - microsecondTimestampPostSet,
            total: microsecondTimestampPostGet - microsecondTimestamp
          });
        }

        writeReadStoragePerformance();
      }
    }
    const urlParams = new URL(window.location.href).searchParams.get('debug');
    let debugParam = urlParams ? '?debug=' + urlParams : '';

    let url = '/cdg/v2/async/request' + debugParam;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Error Occurred');
        }
        return res.json();
      })
      .then((json) => handleRequestSuccess(json))
      .catch((error) => {
        dispatch({
          value: true,
          input: 'submitNotProcessing',
          valid: true
        });
        dispatch({
          value: true,
          input: 'submitError',
          valid: true
        });
        scrollToRef(topOfFormRef);
      });

    startTimer();
  };

  const handleSubmitAnswerSuccess = (json) => {

    if (json) {

      dispatch({
        value: json.lnResponse.transactionStatus,
        input: 'lexisNexisStatus',
        valid: true
      });
      dispatch({
        value: json,
        input: 'response',
        valid: true
      });
    }
  };

  const handleSubmitLexisNexisAnswers = (event, currentState) => {
    event.preventDefault();
    const { response } = state;
    const responseBody = response.value;

    const requestBody = {
      oneTrustTraceId: responseBody.oneTrustTraceId,
      questionSetId: responseBody.lnResponse.questionSet.questionSetId,
      firstName: responseBody.firstName,
      lastName: responseBody.lastName,
      phone: responseBody.phone,
      requestTypes: responseBody.requestTypes,
      pendingLnTransactionInfo: {
        conversationId: responseBody.lnResponse.conversationId,
        lexId: responseBody.lnResponse.lexId,
        requestId: responseBody.lnResponse.requestId
      },
      questions: responseBody.lnResponse.questionSet.questions.map(
        (ele) => {
          return {
            questionId: ele.questionId,
            choice: currentState[ele.questionId].value
          };
        }
      )
    };
    dispatch({
      value: false,
      input: 'submitNotProcessing',
      valid: false
    });
    fetch('/cdg/v2/async/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Error Occured');
        }
        return res.json();
      })
      .then((json) => handleSubmitAnswerSuccess(json))
      .catch((error) => {
        dispatch({
          value: true,
          input: 'submitNotProcessing',
          valid: true
        });
        dispatch({
          value: true,
          input: 'submitError',
          valid: true
        });
        dispatch({
          value: false,
          input: 'submitSuccess',
          valid: true
        });
        scrollToRef(topOfFormRef);
      });
  };

  if (state.submitSuccess.value && state.lexisNexisStatus.value !== lexisNexisStatus.pending) {
    const success = (state.lexisNexisStatus.value === lexisNexisStatus.passed);
    return <CpaConfirmation response={state.response} success={success} />;
  }

  if (state.submitSuccess.value && state.lexisNexisStatus.value === lexisNexisStatus.pending) {
    return (
      <CpaLexisNexisQuestions
        response={state.response}
        callback={handleSubmitLexisNexisAnswers}
      />
    );
  }

  const displayPendingMessage = () => {
    setRequestPendingBanner(true);
    scrollToRef(topOfFormRef);
  };

  const startTimer = () => {
    setTimeout(displayPendingMessage, 6000);
  };

  function getPrivacyCorrectionRow() {
    let options = cpraEnabled ? cpraRequestTypeOptions : requestTypeOptions;
    return (
      <Row>
        <CpaRadioGroup
          header="Select Request Type"
          callback={inputChangeHandler}
          name="requestType"
          options={options}
          spacing="2"
        />
      </Row>
    );
  }

  return (
    <div ref={topOfFormRef} data-component="ConsumerPrivacyActForm">
      <script
        src="https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit"
        async
        defer
      />

      <Row>
        <Col xs="12">
          {state.submitError.value
            && (
              <Alert status="error">We’re sorry, due to technical issues, we were not able to process your request.
                Please try again later or call 1-800-394-1326.
              </Alert>
            )}

          <div id="mainContent">
            <div id="mainFormGroup">
              <form
                id="mainForm"
                className="consumer-privacy-act-form"
                onSubmit={handleSubmit}
                data-testid="cpa-form"
              >
                <div>
                  {getPrivacyCorrectionRow()}

                  {state.requestType.value === 'Delete My Information'
                    && (
                      <Row>
                        <Col xs="12">
                          <div className="delete-popup">
                            <Alert status="error">After we complete your delete request,
                              you may no longer be able to access your account or other information
                              regarding your interactions with us.
                            </Alert>
                          </div>
                        </Col>
                      </Row>
                    )}
                </div>

                <div>
                  {state.requestType.value === 'Correct My Information' && cpraEnabled
                    && (
                      <Row>
                        <Col xs="12">
                          <div className="data-correction-info">
                            <h3><b>Data Correction for customers with a Home Depot account</b></h3>
                            <br />
                            If you have an account with us, the fastest way to correct and/or update
                            most of your personal information is to login by clicking below.
                            <br />
                            Information that you can correct includes:
                            <br />
                            <ul>
                              <li>Name</li>
                              <li>Physical Address</li>
                              <li>Email Address</li>
                              <li>Phone Number</li>
                            </ul>
                            <Button
                              onClick={() => navigate('/auth/view/signin?redirect=%2Fmyaccount%2Fprofile')}
                              className="smaller-btn privacy-home--btn"
                              inline
                              outline
                            >My Account
                              {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
                              <img
                                src={imageBaseUrl + '/caret-orange.svg'}
                                alt=""
                                style={{ margin: '0 0 0 15px' }}
                              />
                            </Button>
                            <br />
                            <br />
                            If you are unable to access your account, please call us at:
                            1-800-HOME-DEPOT (1-800-466-3337).
                          </div>
                        </Col>
                      </Row>
                    )}

                  <div>
                    <Row>
                      <Col xs="12">
                        <div>
                          {!state.submitError.value && requestPendingBanner && (
                            <Alert onClose={() => { setRequestPendingBanner(false); }} status="warning">
                              Your request is being created and taking a little longer than usual - you should
                              have your Request ID shortly.
                            </Alert>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs="8">
                        <h4><b>Where is your primary residence?</b></h4>
                        <br />
                        <CpaStateDropdown
                          name="stateOfResidence"
                          callback={inputChangeHandler}
                          header="State/Territory of Primary Residence"
                          selected={state.stateOfResidence.value}
                        />
                      </Col>
                    </Row>
                    <Col xs="8">
                      <hr />
                    </Col>
                  </div>

                  <Row>
                    <Col>
                      <h4><b>What is your name and contact information?</b></h4>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="4">
                      <div className="cpa-input__input-form-header">
                        {formatHeader('First Name')}
                      </div>
                      <Input
                        id="firstName"
                        name="firstName"
                        className="cpa-form-input"
                        errorText="Please enter a valid first name."
                        value={state.firstName.value}
                        onChange={firstNameChangeHandler}
                        validation={defaultValidation}
                      />
                    </Col>
                    <Col sm="4">
                      <div className="cpa-input__input-form-header">
                        {formatHeader('Last Name')}
                      </div>
                      <Input
                        name="lastName"
                        className="cpa-form-input"
                        errorText="Please enter a valid last name."
                        value={state.lastName.value}
                        onChange={lastNameChangeHandler}
                        validation={defaultValidation}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="8">
                      <CpaEmailAddressInput
                        onChangeCallback={emailChangeHandler}
                        value={state.email.value}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="4">
                      <CpaPhoneInput
                        callback={inputChangeHandler}
                        value={state.phone.value}
                        header="Phone (optional)"
                      />
                    </Col>
                  </Row>
                  <Col xs="8">
                    <hr />
                  </Col>

                </div>

                <div>

                  <Row>
                    <Col>
                      <h4><b>What is your address?</b></h4>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="8">
                      <CpaAddressInput
                        name="addressLine1"
                        errorText="Please enter a valid address. PO box addresses are not allowed."
                        header={formatHeader('Street Address Line 1')}
                        value={state.addressLine1.value}
                        callback={inputChangeHandler}
                        viewMode="cpa-input__tooltip-info"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="8">
                      <Input
                        name="addressLine2"
                        header="Street Address Line 2 (optional)"
                        className="cpa-form-input"
                        value={state.addressLine2.value}
                        onChange={addrLine2ChangeHandler}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="8">
                      <div className="cpa-input__input-form-header">
                        {formatHeader('City')}
                      </div>
                      <Input
                        name="city"
                        className="cpa-form-input"
                        errorText="Please enter a valid city."
                        value={state.city.value}
                        onChange={cityChangeHandler}
                        validation={cityValidation}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="12">
                      <InformationalMessage
                        body="If you are submitting a request from a US military APO, FPO, or DPO address,
                          select &quot;Armed Forces America&quot;, &quot;Armed Forces Europe&quot;,
                          or &quot;Armed Forces Pacific&quot; for the State/Territory field."
                        header="APO/FPO/DPO Address"
                      />
                    </Col>
                    <Col sm="4">
                      <CpaStateDropdown
                        name="state"
                        callback={inputChangeHandler}
                        header="State/Territory"
                        selected={state.state.value}
                      />
                    </Col>
                    <Col sm="4">
                      <div className="cpa-input__input-form-header">
                        {formatHeader('ZIP Code')}
                      </div>
                      <Input
                        name="zipCode"
                        className="cpa-form-input"
                        errorText="Please enter a valid ZIP code."
                        value={state.zipCode.value}
                        onChange={zipChangeHandler}
                        validation={zipValidation}
                      />
                    </Col>
                  </Row>

                </div>

                <div>

                  <Row>
                    <Col xs="12">
                      {state.requestType.value === getMyInformationOptionVal
                        && (
                          <CpaRadioGroup
                            header="Access My Information Request: Select Delivery Method"
                            options={deliveryMethodOptions}
                            name="deliveryMethod"
                            callback={inputChangeHandler}
                            validation={validateInformationRequest}
                            spacing="2"
                          />
                        )}
                    </Col>
                  </Row>

                </div>

                {state.requestType.value === 'Correct My Information' && cpraEnabled && (
                  <Col xs="8">
                    <hr />
                  </Col>
                )}

                {state.requestType.value === 'Correct My Information' && cpraEnabled && (
                  <div className="information-correction-field" data-testid="data-correction-form">
                    <Row>
                      <Col sm="4">
                        <h4><b>{formatHeader('What information do you want to correct?')}</b></h4>
                        <br />
                        <div>Please describe the type of account you have, what is to be corrected, and where.</div>
                        <Input
                          name="dataCorrectionComments"
                          type="textarea"
                          placeholder='Example - "I would like to update the email address in my
                           online account from johnsmith22@gmail.com to robertsmith21@gmail.com"'
                          className="cpa-form-input"
                          onChange={dataCorrectionCommentsChangeHandler}
                          validation={dataCorrectionValidation}
                          value={state.dataCorrectionComments.value}
                        />
                        <div>
                          Do not include any sensitive personal information like social security
                          numbers or credit card numbers.
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}

                <div>
                  <Row>
                    <Col xs="12">
                      <CpaRecaptcha
                        callback={inputChangeHandler}
                        siteKey={recaptchaKey}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="4">
                      <CpaSubmitButton
                        isSubmitEnabled={state.submitEnabled.value}
                        isSubmitError={state.submitError.value}
                      />
                    </Col>
                  </Row>
                </div>
              </form>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

};

ConsumerPrivacyActForm.displayName = 'ConsumerPrivacyActForm';

ConsumerPrivacyActForm.propTypes = {
  recaptchaKey: string.isRequired,
  cpraEnabled: bool.isRequired
};

ConsumerPrivacyActForm.defaultProps = {};

export { ConsumerPrivacyActForm };
